import React, { type ComponentProps } from 'react';
import { graphql } from 'react-relay';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ModalTransition } from '@atlaskit/modal-dialog';

import { PeopleAndTeamsConfigurationProvider } from '@atlassian/people-teams-configuration-client';
import { TeamCreateDialog, type InvitedUser } from '@atlassian/people-teams';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	CreateTeamModalQuery$data,
	CreateTeamModalQuery$variables,
} from './__generated__/CreateTeamModalQuery.graphql.ts';

export const CreateTeamModalQuery = graphql`
	query CreateTeamModalQuery {
		user: confluence_atlassianUser(current: true) {
			id
			displayName
			photos {
				value
				isPrimary
			}
		}
	}
`;
export function CreateTeamModal({
	isOpen,
	onClose,
	...props
}: Partial<ComponentProps<typeof TeamCreateDialog>> & {
	isOpen: boolean;
	onClose: () => void;
}) {
	const { cloudId, orgId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data, loading, error } = useQueryInterop<
		CreateTeamModalQuery$data,
		CreateTeamModalQuery$variables
	>(CreateTeamModalQuery);

	if (error) {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'failed',
				actionSubject: 'user data',
				actionSubjectId: 'CreateTeamModalQuery',
				source: 'cc-onboarding-personalized-templates-v2',
				attributes: {
					errorMessage: error.message,
				},
			},
		}).fire();
	}
	const currentUser: InvitedUser | undefined =
		data?.user?.id && data?.user?.displayName
			? {
					id: data.user.id,
					fullName: data.user.displayName,
					avatarUrl: data.user.photos?.[0]?.value ?? undefined,
				}
			: undefined;

	if (loading) {
		return null;
	}

	return (
		<ModalTransition>
			{isOpen ? (
				<PeopleAndTeamsConfigurationProvider product="confluence">
					<TeamCreateDialog
						{...props}
						cloudId={cloudId}
						product="confluence"
						maxSelectedMembers={5}
						orgId={orgId}
						currentUser={currentUser}
						onClose={onClose}
						enableMembershipSettingsChoice
						enableTeamPermissionCheck
						onCreateTeamSuccess={onClose}
						onCreateTeamFailed={onClose}
					/>
				</PeopleAndTeamsConfigurationProvider>
			) : null}
		</ModalTransition>
	);
}
